import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import * as React from "react";
import { DateRange } from "react-day-picker";

import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import { Side } from "../ui/customChervonIcon";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

import { cn } from "@/lib/utils";

// Define the props type to include onChange
type DatePickerWithRangeProps = React.HTMLAttributes<HTMLDivElement> & {
  defaultValue: DateRange | undefined;
  onChange?: (date: DateRange | undefined) => void;
  side?: Side;
};

const getDateRange = (
  date: DateRange | undefined,
  isDateApplied: boolean,
): React.ReactNode => {
  let output: React.ReactNode;
  if (isDateApplied && date?.from) {
    if (date.to) {
      output = (
        <>
          {format(date.from, "LLL dd, y")} - {format(date.to, "LLL dd, y")}
        </>
      );
    } else {
      output = format(date.from, "LLL dd, y");
    }
  } else {
    output = <span className='opacity-50 '>Pick a date</span>;
  }

  return output;
};

const DatePickerWithRange = ({
  className,
  defaultValue,
  onChange,
  side,
}: DatePickerWithRangeProps): React.JSX.Element => {
  const [isOpen, setOpen] = React.useState<boolean>(false);
  const [appliedDate, setAppliedDate] = React.useState<boolean>(false);
  const [date, setDate] = React.useState<DateRange | undefined>({
    from: undefined,
    to: undefined,
  });

  // // Call the onChange prop whenever the date changes
  // React.useEffect(() => {
  //   if (onChange) {
  //     onChange(date);
  //   }
  // }, [date]);

  const handleSetDate = (): void => {
    if (onChange) {
      onChange(date);
      setAppliedDate(true);
    }
    setOpen(false);
  };

  React.useEffect(() => {
    if (defaultValue === null || defaultValue === undefined) {
      setDate({ from: undefined, to: undefined });
      setAppliedDate(false);
    }
  }, [defaultValue]);

  return (
    <div className={cn("grid gap-2 w-fit ", className)}>
      <Popover open={isOpen} onOpenChange={() => setOpen(!isOpen)}>
        <PopoverTrigger asChild>
          <Button
            id='date'
            variant={"outline"}
            className={cn(
              "w-[300px] justify-start text-left font-normal ",
              !date && "text-muted-foreground ",
              className,
            )}
          >
            <CalendarIcon className='w-4 h-4 mr-2 opacity-50' />
            {getDateRange(date, appliedDate)}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0 m-auto' align='start' side={side}>
          <div>
            <Calendar
              mode='range'
              defaultMonth={date?.from}
              selected={date}
              onSelect={setDate}
              numberOfMonths={2}
            />
            <div className='flex flex-row-reverse gap-2 mb-2 mr-4'>
              <Button onClick={handleSetDate}>Apply</Button>
              <Button variant={"ghost"} onClick={() => setOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DatePickerWithRange;
